import { PlusOutlined } from '@ant-design/icons';
import { Button, Flex, Modal, Space, theme, Typography } from 'antd';
import { CircleAlert } from 'lucide-react';
import { memo, type ReactNode, useCallback, useState } from 'react';
import SiteMenuOrdersTable from './site-menu-orders.table';
import SiteMenuOrderWizard from './wizard/site-menu-order-wizard';
import LoadingSpin from '../../../../../components/loading-spin';
import SyncSuspense from '../../../../../components/sync-suspense';
import { type CustomerSetting, type Site } from '../../../../../entities';
import useErrorNotification from '../../../../../hooks/use-error-notification';
import useSiteOrderOptions from '../../../../../hooks/use-site-order-options';
import { type LocalDate } from '../../../../../utils/local-date';
import { type EditableSiteMenuOrder } from '../customer-order-form.utils';

interface SiteMenuOrderFormProps {
  site: Site;
  siteMenuOrders: EditableSiteMenuOrder[];
  customerSetting: CustomerSetting | undefined;
  orderDate: LocalDate | undefined;
  onSiteMenuOrdersChange: (siteId: string, siteMenuOrders: EditableSiteMenuOrder[]) => void;
  onEditingChange: (siteId: string, isEditing: boolean) => void;
}

/**
 * The wizard is closed when undefined, else it adds a site menu order, or edits "siteMenuOrder"
 * when it has one
 */
type WizardState = { siteMenuOrder?: EditableSiteMenuOrder } | undefined;

/** The site menu orders of a site, and the wizard to add or edit them */
const SiteMenuOrderForm = ({
  site,
  siteMenuOrders,
  customerSetting,
  orderDate,
  onSiteMenuOrdersChange,
  onEditingChange,
}: SiteMenuOrderFormProps): ReactNode => {
  const { token } = theme.useToken();
  const [wizard, setWizard] = useState<WizardState>();
  const [selectedSiteMenuOrderId, setSelectedSiteMenuOrderId] = useState<string>();
  const orderOptions = useSiteOrderOptions(site.id, orderDate);
  useErrorNotification(orderOptions.errorMessage);

  const openWizard = useCallback(
    (siteMenuOrder?: EditableSiteMenuOrder): void => {
      setWizard({ siteMenuOrder });
      setSelectedSiteMenuOrderId(siteMenuOrder?.temporaryId);
      onEditingChange(site.id!, true);
    },
    [site.id, onEditingChange]
  );

  const closeWizard = (): void => {
    setWizard(undefined);
    setSelectedSiteMenuOrderId(undefined);
    onEditingChange(site.id!, false);
  };

  const handleConfirm = (confirmedSiteMenuOrder: EditableSiteMenuOrder): void => {
    const isUpdate = siteMenuOrders.some(
      (siteMenuOrder) => siteMenuOrder.temporaryId === confirmedSiteMenuOrder.temporaryId
    );
    onSiteMenuOrdersChange(
      site.id!,
      isUpdate
        ? siteMenuOrders.map((siteMenuOrder) =>
            siteMenuOrder.temporaryId === confirmedSiteMenuOrder.temporaryId
              ? confirmedSiteMenuOrder
              : siteMenuOrder
          )
        : [...siteMenuOrders, confirmedSiteMenuOrder]
    );
    closeWizard();
  };

  const handleDelete = useCallback(
    (siteMenuOrderToDelete: EditableSiteMenuOrder) => {
      setSelectedSiteMenuOrderId(siteMenuOrderToDelete.temporaryId);
      Modal.confirm({
        width: '50%',
        title: 'Confirm deletion',
        icon: <CircleAlert />,
        content: (
          <Typography.Text>
            Are you sure you want to delete{' '}
            <b>{`${siteMenuOrderToDelete.scheduledMeal.mealCategory.name} (${siteMenuOrderToDelete.mainType})`}</b>{' '}
            from the list?
          </Typography.Text>
        ),
        okText: 'Yes',
        okType: 'danger',
        cancelText: 'No',
        onOk: () => {
          onSiteMenuOrdersChange(
            site.id!,
            siteMenuOrders.filter(
              (siteMenuOrder) => siteMenuOrder.temporaryId !== siteMenuOrderToDelete.temporaryId
            )
          );
          setSelectedSiteMenuOrderId(undefined);
        },
        onCancel: () => {
          setSelectedSiteMenuOrderId(undefined);
        },
      });
    },
    [site.id, siteMenuOrders, onSiteMenuOrdersChange]
  );

  return (
    <Flex vertical>
      <SyncSuspense fallback={<LoadingSpin compact />} condition={!orderOptions.isLoading}>
        <SiteMenuOrdersTable
          siteMenuOrders={siteMenuOrders}
          selectedSiteMenuOrderId={selectedSiteMenuOrderId}
          onEdit={openWizard}
          onDelete={handleDelete}
        />
        <Space direction="vertical" style={{ display: 'flex', marginBottom: 8, width: '100%' }}>
          {wizard ? (
            <SiteMenuOrderWizard
              // Starts again when another site menu order is edited
              key={wizard.siteMenuOrder?.temporaryId ?? 'new'}
              initialSiteMenuOrder={wizard.siteMenuOrder}
              site={site}
              customerSetting={customerSetting}
              availableMeals={orderOptions.meals}
              onConfirm={handleConfirm}
              onCancel={closeWizard}
            />
          ) : (
            orderDate &&
            (orderOptions.meals.length > 0 ? (
              <Flex
                justify="center"
                style={{ marginBottom: token.marginLG, marginTop: token.marginLG }}
              >
                <Button
                  type="dashed"
                  onClick={() => {
                    openWizard();
                  }}
                  icon={<PlusOutlined />}
                >
                  Add Meal
                </Button>
              </Flex>
            ) : (
              <Flex justify="center">
                <Typography.Text italic>
                  No meals available for this site on the selected date.
                </Typography.Text>
              </Flex>
            ))
          )}
        </Space>
      </SyncSuspense>
    </Flex>
  );
};

export default memo(SiteMenuOrderForm);
